/**
 * ExportDialog - Export project buffers as markdown
 *
 * Modal with metadata checkboxes (timestamps, sources, transform history,
 * quality scores). Calls IPC to export and prompts save dialog.
 *
 * @module @humanizer/studio/components/workspace/ExportDialog
 */

import { useState, useCallback } from 'react';

interface ExportDialogProps {
  projectId: string;
  projectName: string;
  bufferCount: number;
  onClose: () => void;
  onExportComplete?: (path: string) => void;
}

interface ExportOptions {
  includeTimestamps: boolean;
  includeSources: boolean;
  includeTransformHistory: boolean;
  includeQualityScores: boolean;
}

export function ExportDialog({ projectId, projectName, bufferCount, onClose, onExportComplete }: ExportDialogProps) {
  const [options, setOptions] = useState<ExportOptions>({
    includeTimestamps: true,
    includeSources: true,
    includeTransformHistory: false,
    includeQualityScores: false,
  });
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleOption = useCallback((key: keyof ExportOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleExport = useCallback(async () => {
    if (!window.electronHarvest?.exportMarkdown) {
      setError('Export requires the Electron app');
      return;
    }
    setExporting(true);
    setError(null);

    try {
      const result = await window.electronHarvest.exportMarkdown(projectId, options);
      if (result?.path) {
        onExportComplete?.(result.path);
      }
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [projectId, options, onClose, onExportComplete]);

  return (
    <div className="exploration__overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }} role="presentation">
      <div className="export-dialog">
        <div className="export-dialog__header">
          <h2 className="export-dialog__title">Export Project</h2>
          <button className="export-dialog__close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="export-dialog__body">
          <p className="export-dialog__description">
            Export <strong>{projectName}</strong> ({bufferCount} buffers) as markdown files.
          </p>

          {error && (
            <div className="outline-panel__error" role="alert">{error}</div>
          )}

          <div className="export-dialog__options">
            <h3 className="export-dialog__subtitle">Include in front matter</h3>
            {([
              { key: 'includeTimestamps', label: 'Timestamps' },
              { key: 'includeSources', label: 'Source attribution' },
              { key: 'includeTransformHistory', label: 'Transform history' },
              { key: 'includeQualityScores', label: 'Quality scores' },
            ] as const).map(opt => (
              <label key={opt.key} className="export-dialog__option">
                <input
                  type="checkbox"
                  checked={options[opt.key]}
                  onChange={() => toggleOption(opt.key)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="export-dialog__footer">
          <button
            className="outline-panel__btn outline-panel__btn--secondary"
            onClick={onClose}
            disabled={exporting}
          >
            Cancel
          </button>
          <button
            className="outline-panel__btn outline-panel__btn--primary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export Markdown'}
          </button>
        </div>
      </div>
    </div>
  );
}
